import {
  useState,
  useEffect,
  useRef,
  useMemo,
  type CSSProperties,
  type RefObject,
} from 'react';
import { ToggleButton, ToggleButtonGroup, Typography, Box, styled } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ReactTooltip from 'react-tooltip';
import { useLocation, useNavigate } from 'react-router-dom';

import { fetchWrapper } from '@/lib/fetch-wrapper';
import { ROUTES, urls } from '@/urls';
import DisputesModal from '@/components/disputes-table/disputes-modal';
import { T } from '@/theme/tokens';
import { DsIcon } from '@/components/ds';
import { AppCard } from '@/components/app-card/app-card';
import { Loader } from '@/components/loader/loader';
import DisputeDetailsModal from '../dispute-details/dispute-details-modal';
import ResourceCardV2 from './resource-card-v2';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';
import { getUser } from '@/lib/common';
import RecommendationModal from '@/screens/optimization/components/recommendation-modal';
import { DisputeDialog } from '@/components/dispute-dialog/dispute-dialog';
import { useActionCenterViewContext } from '@/contexts/action-center-view-context';
import { ActionCenterModule } from '@/utils/actionCenterDetails';

const TYPE_TO_PATH: Record<ActionCenterItemV2['action_type'], string> = {
  Allocation: ROUTES.ALLOCATION,
  Budget: ROUTES.BUDGETS,
  Budgets: ROUTES.BUDGETS,
  Attestation: ROUTES.ATTESTATION,
  Optimization: ROUTES.OPTIMIZATION,
  Recommendation: ROUTES.OPTIMIZATION,
};

// Slider arrows styled to match the design: 34px white square, subtle border,
// muted chevron glyph, light-blue hover. Replaces the legacy .slider__button look.
const SliderNavButton = styled('button')({
  width: 34,
  height: 34,
  borderRadius: 8,
  border: `1px solid ${T.borderInput}`,
  background: T.surface,
  color: T.textSoft,
  fontFamily: T.font,
  fontSize: 16,
  lineHeight: 1,
  padding: 0,
  flex: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background 0.15s',
  '&:hover': { background: '#F2F7FD' },
});

interface ActionCenterV2Props {
  endpointUrl: string;
  onItemsLoaded?: (items: ActionCenterItemV2[]) => void;
  scopeFilter?: 'my' | 'supervised' | 'all';
  onScopeFilterChange?: (scope: 'my' | 'supervised' | 'all') => void;
}

export function ActionCenterV2({
  endpointUrl,
  onItemsLoaded,
  scopeFilter: controlledScopeFilter,
  onScopeFilterChange,
}: Readonly<ActionCenterV2Props>) {
  const [internalItemFilter, setInternalItemFilter] = useState<'my' | 'supervised' | 'all'>('all');
  const itemFilter = controlledScopeFilter ?? internalItemFilter;
  const [sortBy, setSortBy] = useState<'date' | 'cost'>('cost');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [dispute, setDispute] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsDialogResource, setDetailsDialogResource] = useState<any>(null);
  const [techOwnerId, setTechOwnerId] = useState<boolean | null | string>(
    false,
  );
  const [ActionCenterDispute, setActionCenterDispute] = useState();
  const [clickedButton, setClickedButton] = useState<any>('');
  const [actionCenterData, setActionCenterData] = useState<ActionCenterItemV2[]>(
    [],
  );
  const [actionCenterDataLoaded, setActionCenterDataLoaded] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerView } = useActionCenterViewContext();

  const user = getUser();

  // Card width is 290 (see ResourceCardV2 StyledCard). CARD_MARGIN_RIGHT mirrors
  // the gap between slider items defined in App.css (.slider__content).
  const CARD_WIDTH = 290;
  const CARD_MARGIN_RIGHT = 8;
  const ITEM_FULL_WIDTH = CARD_WIDTH + CARD_MARGIN_RIGHT;

  useEffect(() => {
    fetchWrapper
      .get(endpointUrl)
      .then((data) => {
        setActionCenterData(data);
        setActionCenterDataLoaded(true);
        if (onItemsLoaded) {
          onItemsLoaded(data);
        }
      })
      .catch((error) => console.error('There was an error!', error));
  }, [endpointUrl, onItemsLoaded]);

  // Calculate counts for each filter type
  const filterCounts = useMemo(() => {
    const currentUserId = user?.id;

    if (!Array.isArray(actionCenterData) || !currentUserId) {
      return { my: 0, supervised: 0, all: 0 };
    }

    const myItemsCount = actionCenterData.filter((item) => {
      return (
        item.responsible_id === currentUserId);
    }).length;

    const supervisedItemsCount = actionCenterData.filter((item) => {
      return item.supervisor_id === currentUserId ||
             item.id_cloud_account_owner === currentUserId ||
             item.sponsor_id === currentUserId
    }).length;

    const allItemsCount = actionCenterData.length;

    return {
      my: myItemsCount,
      supervised: supervisedItemsCount,
      all: allItemsCount,
    };
  }, [actionCenterData, user?.id]);


  // Filter items based on selected filter option
  const actioncenterfiltered = useMemo<ActionCenterItemV2[]>(() => {
    let filtered: ActionCenterItemV2[];

    if (itemFilter === 'all') {
      filtered = actionCenterData;
    } else {
      const currentUserId = user?.id;

      if (itemFilter === 'my') {
        // Show items where user is responsible for the action
        filtered = actionCenterData.filter((item) => {
          return item.responsible_id === currentUserId;
        });
      } else if (itemFilter === 'supervised') {
        filtered = actionCenterData.filter((item) =>
          item.supervisor_id === currentUserId ||
          item.id_cloud_account_owner === currentUserId ||
          item.sponsor_id === currentUserId
        );
      } else {
        filtered = actionCenterData;
      }
    }

    // Sort items based on selected sort option
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'cost') {
        const costA = a.cost ?? 0;
        const costB = b.cost ?? 0;
        return costB - costA; // Descending order (highest cost first)
      } else {
        // Sort by date_created (descending - newest first)
        const dateA = new Date(a.date_created).getTime();
        const dateB = new Date(b.date_created).getTime();
        return dateB - dateA;
      }
    });

    return sorted;
  }, [actionCenterData, itemFilter, sortBy, user]);

  const {
    sliderContainerRef,
    sliderContentRef,
    sliderStyle,
    showSliderButtons,
    maxScrollPosition,
    handleArrowClick,
    xPos,
  } = useHorizontalSlider(
    actioncenterfiltered.length,
    ITEM_FULL_WIDTH,
    CARD_MARGIN_RIGHT,
  );

  const handleFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: 'my' | 'supervised' | 'all' | null,
  ) => {
    if (newFilter !== null) {
      if (onScopeFilterChange) {
        onScopeFilterChange(newFilter);
      } else {
        setInternalItemFilter(newFilter);
      }
    }
  };

  const handleSortChange = (
    _event: React.MouseEvent<HTMLElement>,
    newSort: 'date' | 'cost' | null,
  ) => {
    if (newSort !== null) {
      setSortBy(newSort);
    }
  };

  function openFromParent() {
    setModalIsOpen(true);
  }

  function handleCloseModal(event: any, data: any) {
    setModalIsOpen(false);
  }

  const handleClickOpen = (superid: any, responsible_id: any) => {
    if (superid) {
      fetchWrapper
        .get(urls.DevOps.getDispute.replace(':id', String(superid)))
        .then((response: any) => {
          const disputeData = response?.dispute;
          if (disputeData) {
            setDispute([disputeData]);
            openFromParent();
          }
        })
        .catch((error) => console.error('There was an error!', error));
    } else {
      setTechOwnerId(responsible_id);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTechOwnerId(null);
  };

  const handleDisputeSubmit = (title: string, receiverId: number) => {
    fetchWrapper
      .post(urls.DevOps.createDisputeV2, {
        id_receiver: receiverId,
        sender: user?.username,
        title: title,
        type: clickedButton.action_type,
        action_center_id: clickedButton.id,
      })
      .then(() => {
        fetchWrapper
          .get(urls.DevOps.getMaxDispute)
          .then((maxData: any) => {
            const disputeId = maxData[0]?.disputeid;
            if (disputeId) {
              setActionCenterDispute(disputeId);
              return fetchWrapper.get(urls.DevOps.getDispute.replace(':id', String(disputeId)));
            }
          })
          .then((response: any) => {
            if (response?.dispute) {
              setDispute([response.dispute]);
              setOpen(false);
              openFromParent();
            }
          })
          .catch((error) => console.error('There was an error!', error));
      })
      .catch((error) => console.error('There was an error!', error));
  };

  const openDisputesDialog = (resource: any) => {
    setDetailsDialogOpen(true);
    setDetailsDialogResource(resource);
  };

  const openRecommendationModal = (resource: any) => {
    if (resource.id_recommendation) {
      setSelectedRecommendation({ id: resource.id_recommendation });
    }
  };

  const handleViewItem = (resource: ActionCenterItemV2) => {
    const targetPath = TYPE_TO_PATH[resource.action_type] || location.pathname;
    const targetModule = mapActionTypeToModule(resource.action_type);
    triggerView({
      module: targetModule,
      item: resource,
      originPath: location.pathname,
      targetPath,
    });
    if (targetPath !== location.pathname) {
      navigate(targetPath);
    }
  };

  const handleCloseRecommendationModal = () => {
    setSelectedRecommendation(null);
  };

  return (
    <div className="cardsContainer" style={{ padding: 0, margin: 0 }}>
      <AppCard
        className="card100"
        title={'​'}
        cardBodyStyle={{ padding: 0 }}
        iconTile={false}
        icon={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: T.primaryTint,
                color: T.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 'none',
              }}>
              <DsIcon name="inbox" size={18} />
            </span>
            <div>
              <div style={{ fontWeight: 900, fontSize: 16, color: T.text }}>
                Action Center
              </div>
              <div style={{ fontSize: 12.5, color: T.textFaint2 }}>
                {actioncenterfiltered.length} items need your attention
              </div>
            </div>
          </div>
        }
        headerRight={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '12px',
            }}>
            <ToggleButtonGroup
              value={itemFilter}
              exclusive
              onChange={handleFilterChange}
              size="small"
              color="primary">
              <ToggleButton value="all" disabled={filterCounts.all === 0}>
                All ({filterCounts.all})
              </ToggleButton>
              <ToggleButton value="my" disabled={filterCounts.my === 0}>
                Responsible ({filterCounts.my})
              </ToggleButton>
              <ToggleButton value="supervised" disabled={filterCounts.supervised === 0}>
                Supervised ({filterCounts.supervised})
              </ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                Sort by:
              </Typography>
              <ToggleButtonGroup
                value={sortBy}
                exclusive
                onChange={handleSortChange}
                size="small"
                color="primary">
                <ToggleButton value="cost">
                  <ArrowDownwardIcon sx={{ fontSize: '0.875rem', mr: 0.5 }} />
                  Cost
                </ToggleButton>
                <ToggleButton value="date">
                  <ArrowDownwardIcon sx={{ fontSize: '0.875rem', mr: 0.5 }} />
                  Date
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </div>
        }
        intro="Action Center is where you can find all actions to be taken. Your goal is for the action center to be always empty, that means there are no more costs to save. <br /> You can see different types of actions Allocation / Attestation / Budget or Recommendation. <br /> You can also see who is responsible for the action to be taken, and who is their manager. You can Open a dispute to discuss the cost, or ask why the improvement have not yet been implemented. If you click on View you will be taken to subpage with details of the action to be performed.">
          {!actionCenterDataLoaded && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <Loader minHeight={66} />
          </div>
        )}

        {actionCenterDataLoaded && actioncenterfiltered.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
            }}>
            <div
              style={{
                flex: 1,
                textAlign: 'center',
                padding: 44,
                color: T.textFaint3,
                fontWeight: 700,
              }}>
              You're all caught up.
            </div>
          </div>
        )}

        <ReactTooltip />

        {actionCenterDataLoaded && actioncenterfiltered.length > 0 && (
          <div id="action-center-slider" className="slider p1">
            {showSliderButtons && (
              <SliderNavButton
                type="button"
                aria-label="Scroll left"
                onClick={() => handleArrowClick('left')}
                style={{ visibility: xPos >= 0 ? 'hidden' : 'visible' }}>
                ‹
              </SliderNavButton>
            )}

            <div
              className="slider__container"
              style={{ textAlign: 'left' }}
              ref={sliderContainerRef}>
              <ul
                className="slider__content"
                style={sliderStyle}
                ref={sliderContentRef}>
                {actioncenterfiltered?.map((resource, index) => (
                  <li
                    key={`${resource.id_dispute ?? resource.id}-${
                      resource.action_type
                    }-${index}`}>
                    <ResourceCardV2
                      key={resource.id}
                      resource={resource}
                      dateFilter={0}
                      setClickedButton={setClickedButton}
                      handleClickOpen={handleClickOpen}
                      openDisputesDialog={openDisputesDialog}
                      openRecommendationModal={openRecommendationModal}
                      onViewItem={handleViewItem}
                    />
                  </li>
                ))}
              </ul>
            </div>

            {showSliderButtons && (
              <SliderNavButton
                type="button"
                aria-label="Scroll right"
                onClick={() => handleArrowClick('right')}
                style={{
                  visibility: xPos <= maxScrollPosition ? 'hidden' : 'visible',
                }}>
                ›
              </SliderNavButton>
            )}
          </div>
        )}

        {techOwnerId !== null && (
          <DisputeDialog
            open={open}
            onClose={handleClose}
            onSubmit={handleDisputeSubmit}
            defaultReceiverId={clickedButton.responsible_id}
            currentUserId={user?.id || 0}
            showUserSelector={false}
          />
        )}

        {modalIsOpen && dispute[0] && (
          <DisputesModal
            Dispute={{
              ...(dispute[0] as any),
              ...clickedButton,
              id_dispute: (dispute[0] as any).id,
            }}
            detailsDialogResource={detailsDialogResource}
            IsModalOpened={modalIsOpen}
            onCloseModal={handleCloseModal}
          />
        )}
      </AppCard>

      <DisputeDetailsModal
        open={detailsDialogOpen}
        setOpen={setDetailsDialogOpen}
        resource={detailsDialogResource}
      />

      <RecommendationModal
        recommendation={selectedRecommendation}
        onClose={handleCloseRecommendationModal}
      />
    </div>
  );
}

export default ActionCenterV2;

type HorizontalSliderControls = {
  sliderContainerRef: RefObject<HTMLDivElement | null>;
  sliderContentRef: RefObject<HTMLUListElement | null>;
  sliderStyle: CSSProperties;
  showSliderButtons: boolean;
  maxScrollPosition: number;
  handleArrowClick: (direction: 'left' | 'right') => void;
  xPos: number;
};

function useHorizontalSlider(
  itemCount: number,
  itemFullWidth: number,
  cardMarginRight: number,
): HorizontalSliderControls {
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const sliderContentRef = useRef<HTMLUListElement>(null);
  const [xPos, setXPos] = useState(0);
  const [showSliderButtons, setShowSliderButtons] = useState(false);
  const [maxScrollPosition, setMaxScrollPosition] = useState(0);

  useEffect(() => {
    const updateSliderState = () => {
      if (!sliderContainerRef.current || !sliderContentRef.current) return;

      const containerWidth = sliderContainerRef.current.offsetWidth;
      const totalContentWidth = itemCount * itemFullWidth - cardMarginRight;
      const shouldShowButtons = totalContentWidth > containerWidth;
      setShowSliderButtons(shouldShowButtons);

      const calculatedMaxScroll = -(totalContentWidth - containerWidth);
      setMaxScrollPosition(calculatedMaxScroll);

      setXPos((prevXPos) => {
        let nextPos = prevXPos;
        if (!shouldShowButtons) {
          nextPos = 0;
        } else if (nextPos < calculatedMaxScroll) {
          nextPos = calculatedMaxScroll;
        } else if (nextPos > 0) {
          nextPos = 0;
        }
        return nextPos;
      });
    };

    updateSliderState();

    const resizeObserver = new ResizeObserver(updateSliderState);
    const containerNode = sliderContainerRef.current;
    if (containerNode) {
      resizeObserver.observe(containerNode);
    }

    return () => {
      if (containerNode) {
        resizeObserver.unobserve(containerNode);
      }
    };
  }, [itemCount, itemFullWidth, cardMarginRight]);

  const sliderStyle = useMemo(
    () => ({
      transform: `translateX(${xPos}px)`,
    }),
    [xPos],
  );

  const handleArrowClick = (direction: 'left' | 'right') => {
    if (!sliderContainerRef.current) return;

    const containerWidth = sliderContainerRef.current.offsetWidth;
    const totalContentWidth = itemCount * itemFullWidth - cardMarginRight;
    const maxScroll = -(totalContentWidth - containerWidth);

    setXPos((prevXPos) => {
      if (direction === 'right') {
        return Math.max(prevXPos - itemFullWidth, maxScroll);
      }
      if (direction === 'left') {
        return Math.min(prevXPos + itemFullWidth, 0);
      }
      return prevXPos;
    });
  };

  return {
    sliderContainerRef,
    sliderContentRef,
    sliderStyle,
    showSliderButtons,
    maxScrollPosition,
    handleArrowClick,
    xPos,
  };
}

function mapActionTypeToModule(
  actionType: ActionCenterItemV2['action_type'],
): ActionCenterModule {
  switch (actionType) {
    case 'Allocation':
      return 'Allocation';
    case 'Budget':
    case 'Budgets':
      return 'Budgets';
    case 'Attestation':
      return 'Attestation';
    case 'Recommendation':
    case 'Optimization':
    default:
      return 'Optimization';
  }
}

function mapActionTypeToVisibleText(
  actionType: ActionCenterItemV2['action_type'],
): ActionCenterModule {
  switch (actionType) {
    case 'Allocation':
      return 'Allocation';
    case 'Budget':
    case 'Budgets':
      return 'Budgets';
    case 'Attestation':
      return 'Attestation';
    case 'Recommendation':
    case 'Optimization':
    default:
      return 'Optimization';
  }
}

